import type {
    BrandInterface,
    ColorInterface,
    FillInterface,
    GradientInterface,
    PaletteInterface,
    SpecialGradientInterface,
    SurfaceInterface,
    TextInterface,
} from '../types';

const Text: TextInterface = {
    x10: 'rgba(255, 255, 255, 0.9)',
    x20: 'rgba(255, 255, 255, 0.6)',
    x30: 'rgba(255, 255, 255, 0.45)',
    Black: 'rgba(0, 0, 0, 0.9)',
    White: 'rgba(255, 255, 255, 1)',
    Inverse: 'rgba(0, 0, 0, 0.9)',
};

const Fill: FillInterface = {
    x10: 'rgba(255, 255, 255, 0.9)',
    x20: 'rgba(255, 255, 255, 0.45)',
    x30: 'rgba(255, 255, 255, 0.24)',
    x40: 'rgba(255, 255, 255, 0.14)',
    x50: 'rgba(255, 255, 255, 0.08)',
    x55: 'rgba(255, 255, 255, 0.065)',
    x60: 'rgba(255, 255, 255, 0.05)',
    White: 'rgba(255, 255, 255, 1)',
    Inverse: 'rgba(0, 0, 0, 0.9)',
};

const Surface: SurfaceInterface = {
    x10: 'rgba(20, 20, 20, 1)',
    x20: 'rgba(31, 31, 31, 0.96)',
    x30: 'rgba(41, 41, 41, 1)',
    x40: 'rgba(51, 51, 51, 1)',
    Inverse: 'rgba(255, 255, 255, 1)',
    Overlay: 'rgba(255, 255, 255, 0.2)',
    Foreground: 'rgba(31, 31, 31, 1)',
};

const Sell: ColorInterface = {
    x10: 'rgba(255, 255, 255, 0.9)',
    x20: 'rgba(255, 255, 255, 0.08)',
    x30: 'rgba(255, 255, 255, 0.14)',
    x40: 'rgba(255, 255, 255, 0.45)',
    x50: 'rgba(255, 255, 255, 0.6)',
};

const Purple: ColorInterface = {
    x10: 'rgba(178, 102, 255, 1)',
    x20: 'rgba(178, 102, 255, 0.16)',
    x30: 'rgba(178, 102, 255, 0.32)',
    x40: 'rgba(197, 139, 255, 1)',
    x50: 'rgba(209, 163, 255, 1)',
};

const Blue: ColorInterface = {
    x10: 'rgba(54, 176, 217, 1)',
    x20: 'rgba(54, 176, 217, 0.16)',
    x30: 'rgba(54, 176, 217, 0.32)',
    x40: 'rgba(102, 195, 226, 1)',
    x50: 'rgba(134, 208, 232, 1)',
};

const Teal: ColorInterface = {
    x10: 'rgba(0, 203, 204, 1)',
    x20: 'rgba(0, 203, 204, 0.16)',
    x30: 'rgba(0, 203, 204, 0.32)',
    x40: 'rgba(61, 215, 216, 1)',
    x50: 'rgba(102, 224, 224, 1)',
};

const Green: ColorInterface = {
    x10: 'rgba(10, 194, 133, 1)',
    x20: 'rgba(10, 194, 133, 0.08)',
    x30: 'rgba(10, 194, 133, 0.16)',
    x40: 'rgba(8, 163, 112, 1)',
    x50: 'rgba(6, 116, 80, 1)',
};

const Yellow: ColorInterface = {
    x10: 'rgba(255, 225, 77, 1)',
    x20: 'rgba(255, 225, 77, 0.16)',
    x30: 'rgba(255, 225, 77, 0.32)',
    x40: 'rgba(255, 232, 120, 1)',
    x50: 'rgba(255, 237, 148, 1)',
};
const Orange: ColorInterface = {
    x10: 'rgba(255, 149, 0, 1)',
    x20: 'rgba(255, 149, 0, 0.16)',
    x30: 'rgba(255, 149, 0, 0.32)',
    x40: 'rgba(255, 174, 61, 1)',
    x50: 'rgba(255, 191, 102, 1)',
};

const Red: ColorInterface = {
    x10: 'rgba(255, 64, 159, 1)',
    x20: 'rgba(255, 64, 159, 0.16)',
    x30: 'rgba(255, 64, 159, 0.32)',
    x40: 'rgba(255, 110, 182, 1)',
    x50: 'rgba(255, 140, 197, 1)',
};

const Critical: ColorInterface = Red;

const Yes: ColorInterface = Green;
const No: ColorInterface = Red;

const Gradient: GradientInterface = {
    x10: 'radial-gradient(circle at 30% 120%, #00CBCC 0%, #00CBCC 5%, #408FFF 45%,#D24DFF 60%,#FF4D4D 90%)',
};

const Special: SpecialGradientInterface = {
    Mint: '#00D991',
    Black: 'linear-gradient(135deg, #B2ADA1 0%, #A09C91 25%, #D1CEC7 50%, #8E8A81 75%, #B2ADA1 100%)',
    Gold: '#F2BA49',
    Silver: '#B8CBD9',
    Bronze: '#E5AA95',
    Platinum: 'linear-gradient(135deg, #D9D9D9 0%, #AEAEAE 25%, #E8E8E8 50%, #828282 75%, #D9D9D9 100%)',
    Diamond:
        'linear-gradient(135deg, #95E5E5 0%, #A1A1E5 11.11%, #E5ACE5 22.22%, #F2F2DA 44.44%, #ACE5E5 55.56%, #7478A6 66.67%, #A1A1E5 77.78%, #D9D9C3 88.89%, #95E595 100%)',
    Brand: 'rgba(40, 204, 149, 1)',
};

const Brand: BrandInterface = {
    Primary: 'rgba(40, 204, 149, 1)',
    x20: 'rgba(229, 251, 244, 1)',
    x30: 'rgba(179, 244, 222, 1)',
    x40: 'rgba(128, 236, 200, 1)',
    x50: 'rgba(77, 228, 178, 1)',
    x60: 'rgba(0, 195, 130, 1)',
    x70: 'rgba(0, 152, 102, 1)',
    x80: 'rgba(0, 109, 73, 1)',
    Secondary: 'rgba(0, 65, 43, 1)',
    Tertiary: '#003221',
};

export const Palette_AccessibleDarkMode: PaletteInterface = {
    Text,
    Fill,
    Surface,
    Yes,
    No,
    Sell,
    Critical,
    Purple,
    Blue,
    Teal,
    Green,
    Yellow,
    Orange,
    Red,
    Gradient,
    Special,
    Brand,
};
